import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight, ArrowRight } from "lucide-react";
import { useLoginUser } from "@/hooks/useAuth";
import {
  fetchWantedReflection,
  type WantedReflection,
} from "@/services/wanted/reflection";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogHeader,
} from "@/components/ui/dialog";
import { getReadableShiftCodeTextColor } from "@/utils/fontSizeUtils";

type Shift = WantedReflection["entries"][number]["requested"];
function ShiftLabel({ shift }: { readonly shift: Shift }) {
  return (
    <span className="inline-flex min-w-0 items-center gap-1.5">
      <span
        className="flex size-7 shrink-0 items-center justify-center rounded-md text-xs font-semibold"
        style={{
          backgroundColor: shift.color,
          color: getReadableShiftCodeTextColor(shift.color),
        }}
      >
        {shift.code}
      </span>
      <span className="truncate text-xs font-medium text-foreground">
        {shift.name}
      </span>
    </span>
  );
}
export function MyWantedReflection({
  year,
  month,
}: {
  readonly year: number;
  readonly month: number;
}) {
  const user = useLoginUser();
  const [open, setOpen] = useState(false);
  const query = useQuery({
    queryKey: [
      "wanted-reflection",
      user?.account_id,
      user?.office_id,
      user?.nurse_id,
      year,
      month,
    ],
    queryFn: ({ signal }) => fetchWantedReflection(year, month, signal),
    enabled: !!user,
    staleTime: 60000,
    retry: false,
  });
  const data = query.data;
  const summary = data?.summary;
  const rate = summary?.rate;
  const rateLabel =
    rate == null
      ? "계산 대상 없음"
      : `${rate.toLocaleString("ko-KR", { maximumFractionDigits: 1 })}%`;
  if (query.isPending)
    return (
      <Skeleton className="h-7 w-40" aria-label="원티드 반영률 불러오는 중" />
    );
  if (query.isError)
    return (
      <div
        className="flex items-center gap-2 text-xs text-muted-foreground"
        role="status"
      >
        반영률 조회 실패
        <Button
          size="sm"
          variant="ghost"
          className="h-7 px-2 text-xs"
          onClick={() => void query.refetch()}
        >
          다시 시도
        </Button>
      </div>
    );
  if (!data?.issued || !data.submitted)
    return (
      <span className="text-xs font-normal text-muted-foreground">
        {!data?.issued
          ? "원티드 반영률 · 발행 후 확인"
          : "이달 제출한 원티드가 없습니다."}
      </span>
    );
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-7 rounded-full px-2.5 text-xs font-medium"
        >
          원티드 반영률{" "}
          <span className="font-semibold text-sig">{rateLabel}</span>
          <ChevronRight aria-hidden="true" className="size-3.5" />
        </Button>
      </DialogTrigger>
      <DialogContent className="flex max-h-[85dvh] w-[calc(100vw-32px)] max-w-[560px] flex-col gap-4 p-7">
        <DialogHeader className="gap-0 pr-7 text-left">
          <DialogTitle className="text-xl font-bold text-sig">
            {month}월 원티드 반영 내역{rate != null ? ` ${rateLabel}` : ""}
          </DialogTitle>
          <DialogDescription className="mt-5 text-sm font-semibold text-foreground">
            {summary && rate != null
              ? `원티드 ${summary.granted + summary.rejected}건 중 ${summary.granted}건 반영`
              : "반영률을 계산할 대상이 없습니다."}
          </DialogDescription>
        </DialogHeader>
        {summary && summary.unknown > 0 && (
          <p className="text-xs leading-5 text-muted-foreground">
            판정 불가 {summary.unknown}건은 반영률 계산에서 제외됩니다.
          </p>
        )}
        <ul
          aria-label="날짜별 원티드 반영 결과"
          className="min-h-0 divide-y divide-border overflow-y-auto overscroll-contain rounded-xl border border-border bg-background"
        >
          {[...data.entries]
            .sort((a, b) => a.date.localeCompare(b.date))
            .map((entry) => (
              <li key={entry.date} className="min-w-0 space-y-2 px-4 py-2.5">
                <div className="flex items-center justify-between gap-2">
                  <time
                    dateTime={entry.date}
                    className="text-sm font-semibold tabular-nums text-foreground"
                  >
                    {Number(entry.date.slice(5, 7))}/
                    {Number(entry.date.slice(8, 10))}{" "}
                    {new Date(`${entry.date}T12:00:00`).toLocaleDateString(
                      "ko-KR",
                      { weekday: "short" }
                    )}
                  </time>
                  <span
                    className={`shrink-0 rounded-full px-1.5 py-0.5 text-[11px] font-semibold leading-none ${entry.unknown ? "bg-light-muted text-muted-foreground" : entry.granted ? "bg-emerald-50/70 text-emerald-700" : "bg-rose-50 text-destructive"}`}
                  >
                    {entry.unknown
                      ? "판정 불가"
                      : entry.granted
                        ? "반영"
                        : "미반영"}
                  </span>
                </div>
                <div className="flex min-w-0 items-center gap-2.5">
                  <ShiftLabel shift={entry.requested} />
                  <ArrowRight
                    aria-hidden="true"
                    className="size-4 shrink-0 text-muted-foreground"
                  />
                  <span className="sr-only">배정 결과</span>
                  {entry.assigned ? (
                    <ShiftLabel shift={entry.assigned} />
                  ) : (
                    <span className="flex min-h-7 items-center text-xs text-muted-foreground">
                      확인 대기
                    </span>
                  )}
                </div>
                {entry.comment && (
                  <p className="min-w-0 whitespace-pre-wrap break-words text-xs leading-5 text-muted-foreground">
                    {entry.comment}
                  </p>
                )}
              </li>
            ))}
          {data.entries.length === 0 && (
            <li className="p-3 text-sm text-muted-foreground">
              표시할 요청 내역이 없습니다.
            </li>
          )}
        </ul>
      </DialogContent>
    </Dialog>
  );
}
